import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@deepguide-ai/shared';
import { InteractionPoint } from '../screenshot-extension';
import { InteractionPointsManager } from '../plate-ui/interaction-point';
import { Resizable } from '../resizable';

export interface ScreenshotRendererProps {
  url: string;
  width: number;
  height: number;
  elementBox?: any;
  interactionPoints?: InteractionPoint[];
  cropStyle?: 'cropped' | 'full' | 'none';
  align?: 'left' | 'center' | 'right';
  alt?: string;
  zoomLevel?: number;
  panX?: number;
  panY?: number;
  onZoomChange?: (zoom: number) => void;
  onAlignChange?: (align: 'left' | 'center' | 'right') => void;
  onWidthChange?: (width: number) => void;
  onPanChange?: (panX: number, panY: number) => void;
  readOnly?: boolean;
  isFullScreenshot?: boolean; // For standalone Screenshot elements
  className?: string;
}

export function ScreenshotRenderer({
  url,
  width,
  height,
  interactionPoints = [],
  cropStyle = 'full',
  align = 'center',
  alt = '',
  zoomLevel = 1,
  panX = 0,
  panY = 0,
  onZoomChange,
  onAlignChange,
  onWidthChange,
  onPanChange,
  readOnly = true,
  isFullScreenshot = false,
  className,
}: ScreenshotRendererProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [localPan, setLocalPan] = useState({ x: panX, y: panY });
  const imageRef = React.useRef<HTMLDivElement>(null);

  // Preview panning is local so a read-only editor does not mutate the document.
  const effectivePanX = onPanChange ? panX : localPan.x;
  const effectivePanY = onPanChange ? panY : localPan.y;

  // Reset loaded state when URL changes
  useEffect(() => {
    setImageLoaded(false);
  }, [url]);

  useEffect(() => {
    if (!onPanChange) {
      setLocalPan({ x: panX, y: panY });
    }
  }, [onPanChange, panX, panY]);

  useEffect(() => {
    if (zoomLevel <= 1 && !onPanChange) {
      setLocalPan({ x: 0, y: 0 });
    }
  }, [onPanChange, zoomLevel]);

  const screenshotSize = useMemo(
    () => ({ width, height }),
    [width, height]
  );

  // Puppeteer's PDF capture emulates the `print` media type (see
  // webpage-capture-service.ts) to render the export page — that's the one
  // reliable signal that distinguishes "rendering for a fixed ~794px A4 PDF
  // page" from normal in-app viewing/editing, which can be full-width on a
  // large or retina display and benefits from the larger source image.
  const isPrintCapture = typeof window !== 'undefined' && window.matchMedia?.('print')?.matches;
  const imageRequestWidth = isPrintCapture ? 794 : 1920;
  const imageRequestQuality = isPrintCapture ? 75 : 100;

  const transformOrigin = interactionPoints[0]
    ? `${interactionPoints[0].xPercent}% ${interactionPoints[0].yPercent}%`
    : 'center';
  // translate() uses rendered CSS pixels, not the screenshot's source dimensions.
  const renderedWidth = imageRef.current?.clientWidth || width;
  const renderedHeight = imageRef.current?.clientHeight || height;
  const focusPointOffsetX = interactionPoints[0]
    ? (50 - interactionPoints[0].xPercent) * renderedWidth / 100
    : 0;
  const focusPointOffsetY = interactionPoints[0]
    ? (50 - interactionPoints[0].yPercent) * renderedHeight / 100
    : 0;
  const renderedPanX = effectivePanX + focusPointOffsetX;
  const renderedPanY = effectivePanY + focusPointOffsetY;

  const imageStyle = useMemo(() => {
    const cursor = zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default';

    // Use contain to ensure image fills container exactly (since container has matching aspect ratio)
    // This guarantees 1:1 mapping between image pixels and overlay percentages
    return {
      objectFit: "contain" as const,
      objectPosition: "center",
      width: '100%',
      height: '100%',
      cursor,
    };
  }, [zoomLevel, isDragging]);


  const handleWidthChange = useCallback(
    (newWidth: number) => {
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    },
    [onWidthChange]
  );

  // Handle drag start
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel <= 1) return;

      // Check if we clicked on the image itself, not a resize handle
      const target = e.target as HTMLElement;
      if (!target.tagName || target.tagName !== 'IMG') return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX - renderedPanX, y: e.clientY - renderedPanY });
    },
    [renderedPanX, renderedPanY, zoomLevel]
  );

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;

      if (onPanChange) {
        onPanChange(newPanX - focusPointOffsetX, newPanY - focusPointOffsetY);
      } else {
        setLocalPan({
          x: newPanX - focusPointOffsetX,
          y: newPanY - focusPointOffsetY,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    dragStart,
    focusPointOffsetX,
    focusPointOffsetY,
    isDragging,
    onPanChange,
  ]);

  // For Step integration, we don't need resizable wrapper
  if (!isFullScreenshot) {
    return (
      <div
        ref={imageRef}
        className={cn(
          "relative overflow-hidden rounded-lg border border-border w-full",
          className
        )}
      >
        {/* Aspect ratio spacer — always present to maintain container height */}
        <div
          className="w-full"
          style={{ paddingBottom: `${(screenshotSize.height / screenshotSize.width) * 100}%` }}
        />

        {/* Shimmer while image loads */}
        {!imageLoaded && (
          <div className="absolute inset-0 z-10 overflow-hidden bg-sidebar-border">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          </div>
        )}

        <img
          src={getImageUrl(url, imageRequestWidth, imageRequestQuality)}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          onMouseDown={handleMouseDown}
          className="absolute inset-0 w-full h-full user-select-none transition-transform duration-300"
          style={{
            ...imageStyle,
            transform: zoomLevel > 1 ? `translate(${renderedPanX}px, ${renderedPanY}px) scale(${zoomLevel})` : undefined,
            transformOrigin,
          }}
          draggable={false}
        />

        {interactionPoints.length > 0 && (
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none transition-opacity duration-300"
            style={{
              opacity: imageLoaded ? 1 : 0,
              transform: zoomLevel > 1 ? `translate(${renderedPanX}px, ${renderedPanY}px) scale(${zoomLevel})` : 'none',
              transformOrigin,
            }}
          >
            <InteractionPointsManager
              points={interactionPoints}
              containerWidth={screenshotSize.width}
              containerHeight={screenshotSize.height}
            />
          </div>
        )}
      </div>
    );
  }

  // Full resizable version for standalone Screenshot elements
  return (
    <Resizable
      align={align}
      width={width}
      onWidthChange={handleWidthChange}
      readOnly={readOnly}
    >
      <figure className="group relative m-0 max-w-full">
        <div
          ref={imageRef}
          className="relative overflow-hidden rounded-lg border border-border"
          style={{
            // Container uses same aspect ratio as image, ensuring image fills it completely
            aspectRatio: `${screenshotSize.width} / ${screenshotSize.height}`,
          }}
        >
          {/* Shimmer while image loads */}
          {!imageLoaded && (
            <div className="absolute inset-0 z-10 bg-sidebar-border">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-[shimmer_1.5s_ease-in-out_infinite]" style={{ backgroundSize: '200% 100%' }} />
            </div>
          )}

          <img
            src={getImageUrl(url, imageRequestWidth, imageRequestQuality)}
            alt={alt}
            onLoad={() => setImageLoaded(true)}
            onMouseDown={handleMouseDown}
            className="absolute inset-0 w-full h-full user-select-none transition-transform duration-300"
            style={{
              ...imageStyle,
              transform: zoomLevel > 1 ? `translate(${renderedPanX}px, ${renderedPanY}px) scale(${zoomLevel})` : undefined,
              transformOrigin,
              willChange: 'transform',
            }}
            draggable={false}
          />

          {interactionPoints.length > 0 && (
            <div
              className="absolute top-0 left-0 w-full h-full pointer-events-none transition-opacity duration-300"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: zoomLevel > 1 ? `translate(${renderedPanX}px, ${renderedPanY}px) scale(${zoomLevel})` : 'none',
                transformOrigin,
              }}
            >
              <InteractionPointsManager
                points={interactionPoints}
                containerWidth={screenshotSize.width}
                containerHeight={screenshotSize.height}
              />
            </div>
          )}
        </div>
      </figure>
    </Resizable>
  );
}
